import { useState, useEffect } from "react";
import { usePermissionsData } from "./usePermissionsData";
import { usePermissionsState } from "./usePermissionsState";
import { usePermissionsActions } from "./usePermissionsActions";
import { usePermissionsValidation } from "./usePermissionsValidation";
import { usePermissionsUpdater } from "./usePermissionsUpdater";
import { usePermissionChangeHandler } from "./usePermissionChangeHandler";

/**
 * Main permissions management hook
 * Orchestrates all permission-related functionality
 */
export const usePermissions = (
  selectedGroup,
  permissionsCache,
  setPermissionsCache
) => {
  const [activeSubTab, setActiveSubTab] = useState("forms");
  const [formsContentType, setFormsContentType] = useState("general");
  const [previousGroupId, setPreviousGroupId] = useState(null);

  // Reset to Forms tab when group changes
  useEffect(() => {
    if (selectedGroup?.id !== previousGroupId) {
      // Reset to Forms tab when switching groups
      setActiveSubTab("forms");
      setFormsContentType("general");
      setPreviousGroupId(selectedGroup?.id || null);
    }
  }, [selectedGroup?.id, previousGroupId]);

  // Data management - handles API calls and caching
  const dataHook = usePermissionsData(
    selectedGroup,
    permissionsCache,
    setPermissionsCache,
    activeSubTab,
    formsContentType
  );

  // State management - handles user modifications
  const stateHook = usePermissionsState(
    dataHook.processedPermissions,
    activeSubTab,
    formsContentType,
    selectedGroup?.id
  );

  // Validation logic
  const validation = usePermissionsValidation(
    dataHook.rawApiData,
    stateHook.currentPermissions,
    activeSubTab
  );

  // Update strategies
  const updater = usePermissionsUpdater(activeSubTab, dataHook.rawApiData);

  // Permission change handler
  const changeHandler = usePermissionChangeHandler({
    rawApiData: dataHook.rawApiData,
    currentPermissions: stateHook.currentPermissions,
    trackedChanges: stateHook.trackedChanges,
    activeSubTab,
    formsContentType,
    updater,
    updatePermissions: stateHook.updatePermissions,
    setTrackedChanges: stateHook.setTrackedChanges,
    applySyncChange: stateHook.applySyncChange,
    getCurrentViewKey: stateHook.getCurrentViewKey,
    getAllViewStates: stateHook.getAllViewStates,
    getProcessedStructure: dataHook.getProcessedStructure,
  });

  // Save/Cancel actions
  const actionsHook = usePermissionsActions({
    rawApiData: dataHook.rawApiData,
    currentPermissions: stateHook.currentPermissions,
    trackedChanges: stateHook.trackedChanges,
    selectedGroup,
    setSaving: dataHook.setSaving,
    clearError: dataHook.clearError,
    setError: dataHook.setError,
    saveChanges: stateHook.saveChanges,
    resetState: stateHook.resetState,
    hasAnyChanges: stateHook.hasAnyChanges,
    updateRawDataFromTrackedChanges: dataHook.updateRawDataFromTrackedChanges,
  });

  // Tab change handlers
  const handleSubTabChange = (newTab) => {
    stateHook.saveCurrentViewChanges();
    setActiveSubTab(newTab);
    if (newTab !== "forms_content") {
      setFormsContentType("general");
    }
    dataHook.clearError();
  };

  const handleFormsContentTypeChange = (newType) => {
    stateHook.saveCurrentViewChanges();
    setFormsContentType(newType);
  };

  const isPermissionDisabled = (
    itemId,
    permissionType,
    categoryId = null,
    formId = null
  ) => {
    const contentTypeToPass =
      activeSubTab === "forms_content" ? formsContentType : null;

    return validation.isPermissionDisabled(
      itemId,
      permissionType,
      categoryId,
      formId,
      contentTypeToPass
    );
  };

  return {
    // State
    activeSubTab,
    formsContentType,
    permissions: stateHook.currentPermissions,
    loading: dataHook.loading,
    saving: dataHook.saving,
    savingFromToolbar: dataHook.savingFromToolbar,
    hasChanges: stateHook.hasAnyChanges,
    error: dataHook.error,

    // Actions
    handleSubTabChange,
    setFormsContentType: handleFormsContentTypeChange,
    handlePermissionChange: changeHandler.handlePermissionChange,
    handleSave: actionsHook.handleSave,
    handleCancel: actionsHook.handleCancel,
    clearError: dataHook.clearError,
    isPermissionDisabled,
    loadPermissions: dataHook.loadPermissionsForGroup,
    resetState: stateHook.resetState,

    // Expose for debugging (can be removed in production)
    rawApiData: dataHook.rawApiData,
    getAllViewStates: stateHook.getAllViewStates,
    allProcessedStructures: dataHook.allProcessedStructures,
    getProcessedStructure: dataHook.getProcessedStructure,
  };
};
